import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FacultyLayout } from '../../components/FacultyLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Upload as UploadIcon, Trash2, X, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FacultyMaterials = () => {
    const { token } = useAuth();
    const [materials, setMaterials] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [formData, setFormData] = useState({
        course_id: '',
        title: '',
        description: '',
        subject: '',
        file: null
    });

    const fetchData = useCallback(async () => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [materialsRes, coursesRes] = await Promise.all([
                axios.get(`${API_URL}/api/faculty/my-materials`, { headers }),
                axios.get(`${API_URL}/api/faculty/my-courses`, { headers })
            ]);
            setMaterials(materialsRes.data);
            setCourses(coursesRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load materials');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFileChange = (e) => {
        setFormData({ ...formData, file: e.target.files[0] });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.file || !formData.course_id || !formData.title) {
            toast.error('Please fill all required fields');
            return;
        }

        setUploading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };

            // Upload file first
            const fileFormData = new FormData();
            fileFormData.append('file', formData.file);
            const uploadRes = await axios.post(`${API_URL}/api/uploads?file_type=study_material`, fileFormData, { headers });

            // Create study material entry
            await axios.post(
                `${API_URL}/api/study-materials`,
                {
                    course_id: formData.course_id,
                    title: formData.title,
                    description: formData.description,
                    subject: formData.subject,
                    file_url: uploadRes.data.file_url
                },
                { headers }
            );

            toast.success('Study material uploaded successfully');
            setShowModal(false);
            setFormData({ course_id: '', title: '', description: '', subject: '', file: null });
            fetchData();
        } catch (error) {
            console.error('Error uploading material:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload material');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (materialId) => {
        if (!window.confirm('Are you sure you want to delete this material?')) return;

        try {
            const headers = { Authorization: `Bearer ${token}` };
            await axios.delete(`${API_URL}/api/study-materials/${materialId}`, { headers });
            toast.success('Material deleted successfully');
            fetchData();
        } catch (error) {
            console.error('Error deleting material:', error);
            toast.error('Failed to delete material');
        }
    };

    const getCourseName = (courseId) => {
        const course = courses.find(c => c.id === courseId);
        return course?.name || 'Unknown Course';
    };

    // Group materials by course
    const materialsByCourse = materials.reduce((acc, material) => {
        const courseId = material.course_id;
        if (!acc[courseId]) {
            acc[courseId] = [];
        }
        acc[courseId].push(material);
        return acc;
    }, {});

    return (
        <FacultyLayout title="Study Materials">
            <div className="space-y-6">
                <div className="flex justify-end">
                    <Button onClick={() => setShowModal(true)}>
                        <UploadIcon className="w-4 h-4 mr-2" />
                        Upload Material
                    </Button>
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2].map((i) => (
                            <Card key={i} className="animate-pulse">
                                <CardContent className="h-32"></CardContent>
                            </Card>
                        ))}
                    </div>
                ) : materials.length > 0 ? (
                    <div className="space-y-6">
                        {Object.entries(materialsByCourse).map(([courseId, courseMaterials]) => (
                            <Card key={courseId}>
                                <CardHeader>
                                    <CardTitle>{getCourseName(courseId)}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {courseMaterials.map((material) => (
                                            <div
                                                key={material.id}
                                                className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50"
                                            >
                                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                                    <FileText className="w-5 h-5 text-primary" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-semibold text-sm truncate">{material.title}</h4>
                                                    {material.subject && (
                                                        <Badge variant="secondary" className="mt-1 text-xs">
                                                            {material.subject}
                                                        </Badge>
                                                    )}
                                                    {material.description && (
                                                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                                            {material.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button size="sm" variant="outline" asChild>
                                                        <a href={material.file_url} target="_blank" rel="noopener noreferrer">
                                                            View
                                                        </a>
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="destructive"
                                                        onClick={() => handleDelete(material.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card>
                        <CardContent className="py-16 text-center">
                            <FileText className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                            <h3 className="font-heading text-xl font-semibold mb-2">No Materials Uploaded</h3>
                            <p className="text-muted-foreground mb-4">
                                Upload study materials to help your students learn better.
                            </p>
                            <Button onClick={() => setShowModal(true)}>
                                <UploadIcon className="w-4 h-4 mr-2" />
                                Upload Material
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Upload Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-md">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Upload Study Material</CardTitle>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="p-1 hover:bg-gray-100 rounded"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Course *</label>
                                    <select
                                        value={formData.course_id}
                                        onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        required
                                    >
                                        <option value="">Select a course</option>
                                        {courses.map((course) => (
                                            <option key={course.id} value={course.id}>
                                                {course.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Title *</label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        placeholder="e.g., Chapter 1 - Mechanics Notes"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Subject</label>
                                    <input
                                        type="text"
                                        value={formData.subject}
                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        placeholder="e.g., Physics"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Description</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        rows="2"
                                        placeholder="Optional description"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">File *</label>
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        required
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Supported: PDF, DOC, DOCX, PPT, PPTX, etc.
                                    </p>
                                </div>

                                <div className="flex gap-2">
                                    <Button type="submit" disabled={uploading} className="flex-1">
                                        {uploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <UploadIcon className="w-4 h-4 mr-2" />
                                                Upload
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowModal(false)}
                                        disabled={uploading}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}
        </FacultyLayout>
    );
};

export default FacultyMaterials;
